import React, { startTransition, useState } from "react";
import { Modal, Tooltip } from "antd";
import { useDashboardLayout, DASHBOARD_LAYOUTS } from "../../store/context/dashboard-layout-context";
import { useTranslation } from "react-i18next";
import { useTenant } from "../../store/context/tenant-context";
import { darkenHex, hexToRgba } from "../../utils/helper/colorUtils";

const getLayoutOptions = (primary) => {
  const darkPrimary = darkenHex(primary, 0.45);
  return [
    {
      key: DASHBOARD_LAYOUTS.DEFAULT,
      label: "Classic",
      description: "Original layout. Clean and familiar.",
      preview: (
        <div className="w-full h-full bg-gray-50 rounded-lg p-2 flex gap-2">
          <div className="w-1/4 rounded-md h-full" style={{ background: darkPrimary }} />
          <div className="flex-1 flex flex-col gap-1.5">
            <div className="h-3 bg-gray-200 rounded w-3/4" />
            <div className="flex gap-1.5 flex-1">
              <div className="flex-1 bg-white rounded-md border border-gray-200" />
              <div className="w-1/3 bg-white rounded-md border border-gray-200" />
            </div>
          </div>
        </div>
      ),
    },
    {
      key: DASHBOARD_LAYOUTS.MODERN_GRADIENT,
      label: "Modern",
      description: "Bold gradient hero card with stat pills.",
      preview: (
        <div className="w-full h-full bg-gray-50 rounded-lg p-2 flex gap-2">
          <div className="w-1/4 rounded-md h-full" style={{ background: darkPrimary }} />
          <div className="flex-1 flex flex-col gap-1.5">
            <div
              className="h-10 rounded-md"
              style={{ background: `linear-gradient(135deg, ${darkenHex(primary, 0.45)}, ${darkenHex(primary, 0.72)}, ${primary})` }}
            />
            <div className="flex gap-1 h-5">
              <div className="flex-1 rounded" style={{ background: hexToRgba(primary, 0.15) }} />
              <div className="flex-1 bg-orange-100 rounded" />
              <div className="flex-1 bg-blue-100 rounded" />
            </div>
            <div className="flex gap-1.5 flex-1">
              <div className="flex-1 bg-white rounded-md border border-gray-200" />
              <div className="w-1/3 bg-white rounded-md border border-gray-200" />
            </div>
          </div>
        </div>
      ),
    },
    {
      key: DASHBOARD_LAYOUTS.GLASSMORPHISM,
      label: "Dark Glass",
      description: "Immersive dark theme with frosted glass cards.",
      preview: (
        <div
          className="w-full h-full rounded-lg p-2 flex gap-2"
          style={{ background: "linear-gradient(135deg,#0a1628,#111827,#1a1a2e)" }}
        >
          <div className="w-1/4 rounded-md h-full" style={{ background: "rgba(0,0,0,0.3)" }} />
          <div className="flex-1 flex flex-col gap-1.5">
            <div
              className="h-10 rounded-md"
              style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.12)", borderLeft: `3px solid ${primary}` }}
            />
            <div className="flex gap-1 h-5">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex-1 rounded" style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.1)" }} />
              ))}
            </div>
            <div className="flex gap-1.5 flex-1">
              <div className="flex-1 rounded-md" style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)" }} />
              <div className="w-1/3 rounded-md" style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)" }} />
            </div>
          </div>
        </div>
      ),
    },
    {
      key: DASHBOARD_LAYOUTS.MINIMAL,
      label: "Minimal",
      description: "Clean white with a solid balance strip and wide quick actions.",
      preview: (
        <div className="w-full h-full bg-gray-50 rounded-lg p-2 flex gap-2">
          <div className="w-1/4 rounded-md h-full" style={{ background: darkPrimary }} />
          <div className="flex-1 flex flex-col gap-1.5">
            <div className="h-10 rounded-md" style={{ background: darkPrimary }} />
            <div className="flex gap-1 h-4">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex-1 bg-white rounded-lg border border-gray-200" />
              ))}
            </div>
            <div className="flex gap-1.5 flex-1">
              <div className="flex-1 bg-white rounded-md border border-gray-200" />
              <div className="w-1/3 bg-white rounded-md border border-gray-200" />
            </div>
          </div>
        </div>
      ),
    },
  ];
};

const DashboardLayoutSwitcher = () => {
  const [open, setOpen] = useState(false);
  const { layout, changeLayout } = useDashboardLayout();
  const { t } = useTranslation();
  const tenant = useTenant();
  const primary = tenant?.theme?.colorPrimary || "#0b3d2e";
  const LAYOUT_OPTIONS = getLayoutOptions(primary);

  const current = LAYOUT_OPTIONS.find((o) => o.key === layout) || LAYOUT_OPTIONS[0];

  return (
    <>
      <Tooltip title={t("Change dashboard layout")}>
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 border border-gray-200 hover:bg-gray-200 transition-colors text-xs font-semibold text-gray-600 cursor-pointer"
        >
          🎨 {current.label}
        </button>
      </Tooltip>

      <Modal
        title={
          <div>
            <p className="text-base font-bold">{t("Choose Dashboard Layout")}</p>
            <p className="text-xs text-gray-400 font-normal mt-0.5">
              {t("Your preference is saved automatically")}
            </p>
          </div>
        }
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={600}
      >
        <div className="grid grid-cols-2 gap-4 mt-4">
          {LAYOUT_OPTIONS.map((opt) => {
            const isActive = layout === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => {
                  setOpen(false);
                  // Close the chooser in the current frame, then let React
                  // render the comparatively large dashboard layout as a
                  // non-urgent update. This keeps the rest of the portal
                  // responsive while a style is being applied.
                  startTransition(() => changeLayout(opt.key));
                }}
                style={isActive ? { borderColor: primary, background: hexToRgba(primary, 0.06) } : {}}
                className={`text-left rounded-2xl p-3 border-2 transition-all cursor-pointer w-full ${
                  isActive ? "border-2" : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                }`}
              >
                {/* Mini preview */}
                <div className="w-full h-24 rounded-xl overflow-hidden mb-3 border border-gray-100">
                  {opt.preview}
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold" style={{ color: isActive ? primary : "#1f2937" }}>
                      {opt.label}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">{opt.description}</p>
                  </div>
                  {isActive && (
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs flex-shrink-0"
                      style={{ background: primary }}
                    >
                      ✓
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </Modal>
    </>
  );
};

export default DashboardLayoutSwitcher;
